<template>
    <v-dialog
        v-model="showSendDialog"
        width="500"
        scrollable
        :fullscreen="this.$vuetify.breakpoint.xsOnly"
    >
        <template v-slot:activator="{ on, attrs }">
            <v-icon
                class="material-icons-outlined mr-7"
                :disabled="sendTicketDisabled"
                v-bind="attrs"
                @click="showDialog"
            >
                send
            </v-icon>
        </template>
        <v-card id="send-ticket-dialog">
            <v-card-title class="text-h5 lighten-2">
                <v-icon
                    class="material-icons-outlined"
                    color="primary"
                >
                    send
                </v-icon>
                <span>Send Ticket ({{ selectedTickets.length }})</span>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-0">
                <v-tabs
                    v-model="tab"
                >
                    <v-tab
                        class="ml-24"
                        @click="changeMode('email')"
                    >
                        E-Mail
                    </v-tab>
                    <v-tab-item>
                        <v-card flat>
                            <v-card-text>
                                <div class="send-email-subtitle">
                                    Add one or more email addresses.
                                </div>
                                <v-combobox
                                    ref="emailChipsCombobox"
                                    v-model="emailChips"
                                    multiple 
                                    label="Mail To"
                                    outlined
                                    append-icon
                                    chips
                                    deletable-chips
                                    class="tag-input"
                                    :rules="comboEmailRules"
                                    @change="changeEmailChips"
                                />
                                <v-divider />
                                <div class="saved-email-title">
                                    Saved E-Mail Addresses
                                </div>
                                <v-btn
                                    v-if="!showAddEmailControls"
                                    style="text-transform: none !important"
                                    outlined
                                    light
                                    @click="showAddEmailControls = true"
                                >
                                    + Add E-Mail
                                </v-btn>
                                <div
                                    v-if="showAddEmailControls"
                                    class="add-email-controls"
                                >
                                    <div class="add-email-title">
                                        Add Email Address
                                    </div>
                                    <v-text-field
                                        ref="newNameTextField"
                                        v-model="newName"
                                        outlined
                                        label="Name"
                                        :rules="nameRules"
                                    />
                                    <v-text-field
                                        ref="newEmailTextField"
                                        v-model="newEmail"
                                        outlined
                                        label="E-Mail Address"
                                        :rules="emailRules"
                                    />
                                    <div>
                                        <v-btn
                                            style="text-transform: none !important"
                                            outlined
                                            light
                                            @click="cancelSaveEmail"
                                        >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            style="text-transform: none !important"
                                            depressed
                                            color="primary"
                                            @click="saveEmail"
                                        >
                                            Save
                                        </v-btn>
                                    </div>
                                </div>
                            </v-card-text>
                            <div v-if="savedEmails.length">
                                <v-card-text v-if="this.$vuetify.breakpoint.smAndUp">
                                    <v-data-table 
                                        id="saved-email-table"
                                        v-model="selectedSavedEmails"
                                        :headers="savedEmailTableHeaders"
                                        :items="savedEmails"
                                        item-key="email"
                                        show-select
                                        hide-default-footer
                                        @item-selected="savedEmailSelect"
                                        @toggle-select-all="savedEmailSelectAll"
                                    >
                                        <template v-slot:[`item.actions`]="{ item }">
                                            <v-icon
                                                @click="removeSavedEmailClick(item)"
                                            >
                                                delete_outline
                                            </v-icon>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                                <v-data-table
                                    v-else
                                    id="saved-mobile-email-table"
                                    v-model="selectedSavedEmails"
                                    :headers="savedEmailTableMobileHeaders"
                                    :items="savedEmails"
                                    item-key="email"
                                    show-select
                                    hide-default-header
                                    hide-default-footer
                                    mobile-breakpoint="0"
                                    class="my-4"
                                    @item-selected="savedEmailSelect"
                                >
                                    <template v-slot:[`item.name`]="{ item }">
                                        <div>{{ item.name }}</div>
                                        <div>{{ item.email }}</div>
                                    </template>
                                    <template v-slot:[`item.actions`]="{ item }">
                                        <v-icon
                                            @click="removeSavedEmailClick(item)"
                                        >
                                            delete_outline
                                        </v-icon>
                                    </template>
                                </v-data-table>
                            </div>
                            <v-card-text
                                v-else
                                class="saved-email-subtitle"
                            >
                                There are no saved e-mail addresses.
                            </v-card-text>
                        </v-card>
                        <div class="mb-14" />
                    </v-tab-item>
                    <v-tab
                        v-if="supplementalEmailSms"
                        class="ml-24" 
                        @click="changeMode('sms')"                        
                    >
                        SMS/TEXT
                    </v-tab>
                    <v-tab-item>
                        <v-card flat>
                            <v-card-text>
                                <v-form ref="smsForm">
                                    <div class="send-email-subtitle">
                                        Add SMS Destination
                                    </div>
                                    <v-text-field
                                        ref="smsDestinationTextField"
                                        v-model="smsDestination"
                                        maxlength="16"
                                        outlined
                                        :rules="smsRules"
                                        label="SMS Destination"
                                    />
                                </v-form>
                            </v-card-text>                            
                        </v-card>
                    </v-tab-item>
                </v-tabs>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn
                    style="text-transform: none !important"
                    outlined
                    light
                    :width="this.$vuetify.breakpoint.xs ? '160px' : '66px'"
                    @click="cancelSendTickets"
                >
                    Cancel
                </v-btn>
                <v-btn
                    style="text-transform: none !important"
                    depressed
                    color="primary"
                    :disabled="isDisabled"
                    :width="buttonWidth"
                    @click="sendTickets"
                >
                    Send
                </v-btn>
                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
            </v-card-actions>
        </v-card>
        <v-dialog
            id="confirm-remove-popup"
            v-model="showConfirmRemove"
            max-width="400px"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Delete
                </v-card-title>
                <v-divider />
                <v-card-text>
                    Are you sure you want to delete "{{ emailToRemove.name }} - {{ emailToRemove.email }}" from the saved list?
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        style="text-transform: none !important"
                        outlined
                        light
                        @click="closeConfirmRemove"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        @click="removeSavedEmail"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>    
</template>
<script>
import webtmsBackend from '@/services/webtmsBackend';
import { getUserOption } from '../utils/options';
import { hasPermission } from '@/utils/permissions';
import { validatePhoneNumber } from '@/utils/phone.js';

export default {
    props: {
        selectedTickets: {
            type: Array,
            default: () => ([]),
        },
    },
    data: () => ({
        showSendDialog: false,
        showAddEmailControls: false,
        tab: 0,
        search: '',
        emailChips: [],
        newName: '',
        newEmail: '',
        savedEmailTableHeaders: [{
            text: 'Name',
            value: 'name',
        }, {
            text: 'Email Address',
            value: 'email',
        }, {
            text: '',
            value: 'actions',
            sortable: false,
        }],
        savedEmailTableMobileHeaders: [{
            text: '',
            value: 'name',
        }, {
            text: '',
            value: 'actions',
            sortable: false,
        }],
        selectedSavedEmails: [],
        showConfirmRemove: false,
        emailToRemove: {},
        nameRules: [
            v => { return !!v || 'Name is required';}
        ],
        emailRules: [
            v => { return /^.+@.+..+$/.test(v) || 'Invalid Email address'; }
        ],
        comboEmailRules: [
            v => {
                if(Array.isArray(v)) {
                    for(let email of v) {
                        if(!/^.+@.+..+$/.test(email)) {
                            return 'Invalid Email address';
                        }
                    }
                    return true;
                }
            }
        ],
        supplementalEmailSms: hasPermission('SUPPLEMENTAL_EMAIL--SMS'),
        smsDestination: '',
        smsRules: [
            v => validatePhoneNumber(v)
        ],
        mode: 'email',
    }),
    computed: {
        sendTicketDisabled(){
            return this.selectedTickets.length === 0;
        },
        savedEmails() {
            let addresses = getUserOption('tickets-send-mailto');
            if(addresses) {
                try {
                    const parsedAddresses = JSON.parse(addresses);
                    return parsedAddresses;
                } catch(error) {
                    console.error('cannot parse ', addresses, ' due to ', error);
                }
            }
            return [];
        },
        isDisabled() {
            if (this.mode === 'email') {
                return !this.emailChips.length || !this.$refs.emailChipsCombobox.valid;
            } else if (this.mode === 'sms') {               
                return !this.smsDestination?.match(/\S/)  || !this.$refs.smsDestinationTextField.valid;
            }
            return true;
        },
        buttonWidth() {
            return this.$vuetify.breakpoint.xs ? '160px' : '66px';
        }
    },
    methods: {
        showDialog() {
            this.showSendDialog = true;
        },
        sendTicket() {
            this.showSendDialog = false;
        },
        closeDialog() {
            this.showSendDialog = false;
        },
        savedEmailSelect(selectedItem) {
            const selectedItemChecked = selectedItem.value;
            const savedEmail = selectedItem.item.email;
            const isChip = this.emailChips.includes(savedEmail);
            if(selectedItemChecked && !isChip) {
                // add
                this.emailChips.push(savedEmail);
                return;
            } 
            if(!selectedItemChecked && isChip) {
                // remove
                this.emailChips = this.emailChips.filter(chip => chip !== savedEmail);
            }
        },
        savedEmailSelectAll(selectAll) {
            for(let item of selectAll.items) {
                this.savedEmailSelect({
                    item,
                    value: selectAll.value,
                });
            }
        },
        changeEmailChips() {
            for(let savedEmail of this.savedEmails) {
                const emailAddress = savedEmail.email;
                if(this.emailChips.includes(emailAddress)) {
                    if(!this.selectedSavedEmails.find(selectedSavedEmail => selectedSavedEmail.email === emailAddress)) {
                        // add
                        this.selectedSavedEmails.push(savedEmail);
                    }
                } else {
                    // remove
                    this.selectedSavedEmails = this.selectedSavedEmails.filter(selectedSavedEmail => selectedSavedEmail.email !== savedEmail.email);
                }
                
            }
        },
        sendEmailAddresses() {
            let options = getUserOption('tickets-send-mailto') || '[]';
            return  JSON.parse(options);
        },
        cancelSaveEmail() {
            this.newName = '';
            this.newEmail = '';
            this.showAddEmailControls = false;
        },
        cancelSendTickets() {
            this.smsDestination = '';
            if (this.$refs.smsForm) {
                this.$refs.smsForm.reset();
            }
            if(this.$refs.emailForm) {
                this.$refs.emailForm.reset();
            }  
            this.showSendDialog = false;
        },
        async saveEmail() {
            if(!this.newName.length) {
                this.$refs.newNameTextField.focus();
                this.$refs.newNameTextField.blur();
                return;
            }
            if(!this.newEmail.length
            || !this.$refs.newEmailTextField.valid) {
                this.$refs.newEmailTextField.focus();
                this.$refs.newEmailTextField.blur();
                return;
            }
            try {
                const newOption = {
                    name: this.newName,
                    email: this.newEmail,
                };
                let options = this.sendEmailAddresses();
                options.push(newOption);
                options = [['tickets-send-mailto', JSON.stringify(options)]];
                this.$store.dispatch('updateUserOptions', options);
            } catch(error) {
                console.error('saveEmail failed with error ', error);
            }
        },
        removeSavedEmailClick(emailToRemove) {
            this.emailToRemove = emailToRemove;
            this.showConfirmRemove = true;
        },
        closeConfirmRemove() {
            this.emailToRemove = {};
            this.showConfirmRemove = false;
        },
        async removeSavedEmail() {
            let options = this.sendEmailAddresses();
            options = options.filter(mailTo => {
                return mailTo.email != this.emailToRemove.email;
            });
            options = [['tickets-send-mailto', JSON.stringify(options)]];
            await this.$store.dispatch('updateUserOptions', options);
            this.emailToRemove = {};
            this.showConfirmRemove = false;
        },
        async sendTickets() {
            let payload = {
                format: this.mode ==='email'? 'ORIGINAL': 'SMSTEXT',
                destination: this.mode ==='email'? this.emailChips : this.smsDestination,
                tickets: this.selectedTickets.map(ticket => {
                    return {
                        tid: ticket.tid,
                        member_code: ticket.TR_member_code,
                    };
                }),
            };
            console.log('sendTickets payload ', payload);
            try {
                let result = null;
                if(this.mode === 'email') {
                    result = await webtmsBackend.emailTickets(payload);
                } else {
                    result = await webtmsBackend.smsTickets(payload);
                }               
                if(result.err) {
                    throw result.err;
                }
                this.showSendDialog = false;
            } catch(error) {
                console.error('sendTickets failed with ', error);
            }
        },       
        changeMode(mode) {
            this.mode = mode;
            // if (this.$refs.smsForm) {
            //     this.$refs.smsForm.reset();
            // }
            // if(this.$refs.emailForm) {
            //     this.$refs.emailForm.reset();
            // }
            this.emailChips= [];
            this.selectedSavedEmails = [];
            this.smsDestination = '';
            // set focus to SMS destination field
            if(mode === 'sms') {
                setTimeout(() => {
                    console.log('setting focus to smsDestinationTextField');
                    this.$refs.smsDestinationTextField.focus();
                }, 50);
            } else if(mode === 'email') {
                setTimeout(() => {
                    console.log('setting focus to emailChipsCombobox');
                    this.$refs.emailChipsCombobox.focus();
                }, 50);
            }
        },
    }, 
};
</script>
<style>
#send-ticket-dialog .v-tabs-bar {
    padding: 0 16px;
}
#send-ticket-dialog .v-card__title .v-icon {
    background-color: var(--ll-surface-accent);
    margin-right: 12px;
    border-radius: 50%;
    width: 32px;
    height: 32px;    
}
#send-ticket-dialog .control-title {
    margin-top: 16px;
    font-weight: 500;
    font-size: 16px;
    color: var(--ll-text-brand);
}
#send-ticket-dialog .v-card__title span {
    font-size: 20px;
}
#send-ticket-dialog .send-email-subtitle {
    margin-top:16px;
    margin-bottom: 20px;
    font-size: 12px;
    color: var(--ll-text-primary);
}
#send-ticket-dialog .saved-email-title {
    margin-top: 16px;
    margin-bottom: 24px;
    font-weight: 500;
    font-size: 16px;
    color: var(--ll-text-brand);
}
#send-ticket-dialog .saved-email-subtitle {
    margin-top: 20px;
    font-weight: 400;
    font-size: 12px;
    color: var(--ll-text-primary);
}
#send-ticket-dialog  .tag-input span.v-chip {
    background-color: var(--ll-surface-accent);
}
#send-ticket-dialog .add-email-controls {
    border: 1px solid var(--ll-border);
    border-radius: 4px;
    padding: 16px;
    background-color: var(--ll-surface-subtle);
}
#send-ticket-dialog .add-email-title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 30px;
}
#send-ticket-dialog .add-email-controls button{
    margin-right:16px;
}
#send-ticket-dialog .add-email-controls .v-btn--outlined {
    background-color: var(--ll-surface);
}
#saved-email-table.v-data-table {
    border: 1px solid var(--ll-border);
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    margin-top: 16px;
}
#saved-email-table th {
    background-color: var(--ll-surface-subtle);
}
#saved-mobile-email-table td {
    padding-top: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--ll-border);
}
#saved-mobile-email-table td:nth-child(2) div {
    margin: 6px 0;
}
#saved-mobile-email-table td:last-child {
    text-align: right!important;
}
</style>
